import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, AlertCircle, Building, Mail, Globe, Clock, Bell, AlertTriangle, UserX, X, Lock, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import apiClient from '../services/apiClient';
import { useSubscription } from '../hooks/useSubscription';
import Loader from '../components/ui/Loader';
import ChangePasswordSection from '../components/Settings/ChangePasswordSection';

const GeneralPage = () => {
  const { hasActiveSubscription } = useSubscription();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    companyName: '',
    supportEmail: '',
    websiteUrl: 'https://',
    timezone: 'America/New_York',
    language: 'en',
    emailNotifications: true,
  });

  const [saved, setSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Track unsaved changes
  const savedSettingsRef = useRef(null);
  const hasUnsavedChanges = useCallback(() => {
    if (!savedSettingsRef.current) return false;
    return JSON.stringify(settings) !== JSON.stringify(savedSettingsRef.current);
  }, [settings]);

  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Block browser navigation (tab close, refresh)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Intercept sidebar/link clicks to warn about unsaved changes
  useEffect(() => {
    const handleClick = (e) => {
      const anchor = e.target.closest('a[href]');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('mailto:') || href === '#') return;
      if (!hasUnsavedChanges()) return;
      e.preventDefault();
      e.stopPropagation();
      setPendingNavigation(href);
    };
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [hasUnsavedChanges]);

  // Custom dropdown state
  const [timezoneOpen, setTimezoneOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const timezoneRef = React.useRef(null);
  const languageRef = React.useRef(null);

  const timezoneOptions = [
    { value: 'Pacific/Midway', label: 'UTC-11:00 — Midway' },
    { value: 'Pacific/Honolulu', label: 'UTC-10:00 — Hawaii' },
    { value: 'America/Anchorage', label: 'UTC-09:00 — Alaska' },
    { value: 'America/Los_Angeles', label: 'UTC-08:00 — Pacific Time' },
    { value: 'America/Denver', label: 'UTC-07:00 — Mountain Time' },
    { value: 'America/Chicago', label: 'UTC-06:00 — Central Time' },
    { value: 'America/New_York', label: 'UTC-05:00 — Eastern Time' },
    { value: 'America/Halifax', label: 'UTC-04:00 — Atlantic' },
    { value: 'America/Sao_Paulo', label: 'UTC-03:00 — São Paulo' },
    { value: 'Atlantic/South_Georgia', label: 'UTC-02:00 — Mid-Atlantic' },
    { value: 'Atlantic/Azores', label: 'UTC-01:00 — Azores' },
    { value: 'Europe/London', label: 'UTC+00:00 — London' },
    { value: 'Europe/Paris', label: 'UTC+01:00 — Central Europe' },
    { value: 'Europe/Helsinki', label: 'UTC+02:00 — Eastern Europe' },
    { value: 'Europe/Moscow', label: 'UTC+03:00 — Moscow' },
    { value: 'Asia/Yerevan', label: 'UTC+04:00 — Yerevan' },
    { value: 'Asia/Karachi', label: 'UTC+05:00 — Karachi' },
    { value: 'Asia/Kolkata', label: 'UTC+05:30 — India' },
    { value: 'Asia/Dhaka', label: 'UTC+06:00 — Dhaka' },
    { value: 'Asia/Bangkok', label: 'UTC+07:00 — Bangkok' },
    { value: 'Asia/Shanghai', label: 'UTC+08:00 — China' },
    { value: 'Asia/Tokyo', label: 'UTC+09:00 — Tokyo' },
    { value: 'Australia/Sydney', label: 'UTC+10:00 — Sydney' },
    { value: 'Pacific/Noumea', label: 'UTC+11:00 — Noumea' },
    { value: 'Pacific/Auckland', label: 'UTC+12:00 — Auckland' },
  ];

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
    { value: 'es', label: 'Spanish' },
    { value: 'pt', label: 'Portuguese' },
    { value: 'it', label: 'Italian' },
    { value: 'nl', label: 'Dutch' },
    { value: 'sv', label: 'Swedish' },
    { value: 'no', label: 'Norwegian' },
    { value: 'da', label: 'Danish' },
    { value: 'fi', label: 'Finnish' },
    { value: 'ru', label: 'Russian' },
    { value: 'pl', label: 'Polish' },
    { value: 'cs', label: 'Czech' },
    { value: 'ro', label: 'Romanian' },
    { value: 'hu', label: 'Hungarian' },
    { value: 'uk', label: 'Ukrainian' },
    { value: 'ar', label: 'Arabic' },
    { value: 'tr', label: 'Turkish' },
    { value: 'he', label: 'Hebrew' },
    { value: 'hy', label: 'Armenian' },
    { value: 'zh', label: 'Chinese' },
    { value: 'ja', label: 'Japanese' },
    { value: 'ko', label: 'Korean' },
    { value: 'hi', label: 'Hindi' },
    { value: 'vi', label: 'Vietnamese' },
    { value: 'th', label: 'Thai' },
    { value: 'el', label: 'Greek' },
  ];

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (timezoneRef.current && !timezoneRef.current.contains(e.target)) setTimezoneOpen(false);
      if (languageRef.current && !languageRef.current.contains(e.target)) setLanguageOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Deactivate account modal state
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showDeactivateSuccessModal, setShowDeactivateSuccessModal] = useState(false);
  const [deactivatePassword, setDeactivatePassword] = useState('');
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [deactivateError, setDeactivateError] = useState('');

  // Load general settings on component mount
  useEffect(() => {
    const loadGeneralSettings = async () => {
      try {
        const response = await apiClient.get('/api/general');

        if (response.success && response.data.settings) {
          setSettings(response.data.settings);
          savedSettingsRef.current = response.data.settings;
        }
      } catch (error) {
        // Use defaults if API fails
      } finally {
        setIsLoadingData(false);
      }
    };

    loadGeneralSettings();
  }, []);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.put('/api/general', settings);

      if (response.success) {
        savedSettingsRef.current = { ...settings };
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (error) {
      if (Array.isArray(error?.details) && error.details.length > 0) {
        error.details.forEach((d) => toast.error(d.msg || d.message || 'Validation error'));
      } else {
        toast.error(error?.message || 'Failed to save general settings. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleDeactivateAccount = async () => {
    if (!deactivatePassword) {
      setDeactivateError('Please enter your password');
      return;
    }

    setIsDeactivating(true);
    setDeactivateError('');

    try {
      const response = await apiClient.post('/api/users/deactivate', {
        password: deactivatePassword
      });

      if (response.success) {
        // Close password modal and show success modal
        setShowDeactivateModal(false);
        setShowDeactivateSuccessModal(true);
      }
    } catch (error) {
      setDeactivateError(error.response?.data?.message || 'Invalid password or server error');
    } finally {
      setIsDeactivating(false);
    }
  };

  const openDeactivateModal = () => {
    setShowDeactivateModal(true);
    setDeactivatePassword('');
    setDeactivateError('');
  };

  const closeDeactivateModal = () => {
    setShowDeactivateModal(false);
    setDeactivatePassword('');
    setDeactivateError('');
  };

  if (isLoadingData) {
    return (
      <div className="container-mobile py-6 sm:py-8 flex items-center justify-center min-h-96">
        <Loader size="lg" text="Loading General Settings" />
      </div>
    );
  }

  return (
    <div className="container-mobile py-4 sm:py-6 md:py-8 max-w-5xl mx-auto">
      <div className="mb-6 sm:mb-8 md:mb-12 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-light tracking-tight text-gray-900 dark:text-white mb-2 sm:mb-3">
            General Settings
          </h1>
          <p className="text-sm sm:text-base text-gray-500 dark:text-gray-500 font-light max-w-2xl">Manage your account preferences, localization, and notification settings for your AI assistant.</p>
        </div>
        <button
          onClick={handleSave}
          disabled={isLoading}
          className={`w-full sm:w-auto flex-shrink-0 flex items-center justify-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-medium transition-all duration-200 text-sm sm:text-base text-white ${
            saved
              ? 'bg-green-500'
              : isLoading
                ? 'bg-blue-400 cursor-not-allowed'
                : 'bg-blue-500 hover:bg-blue-600'
          }`}
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Saving...</span>
            </>
          ) : saved ? (
            'Saved!'
          ) : (
            'Save Changes'
          )}
        </button>
      </div>

      <div className="space-y-6 sm:space-y-8 md:space-y-10">
        {/* Company Information */}
        <div className="rounded-[20px] border bg-white dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.06] overflow-hidden">
          <div className="px-5 sm:px-6 md:px-8 py-4 sm:py-5 border-b border-gray-100 dark:border-white/[0.06]">
            <div className="flex items-start sm:items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 rounded-[10px] flex items-center justify-center bg-gray-50 dark:bg-white/[0.04] flex-shrink-0">
                <Building className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 dark:text-gray-500" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-white">Company Information</h3>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-500 font-light mt-0.5 sm:mt-1">Basic information about your organization and support contact</p>
              </div>
            </div>
          </div>
          <div className="p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
                  Company Name
                </label>
                <div className="relative">
                  <Building className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
                  <input
                    type="text"
                    value={settings.companyName}
                    onChange={(e) => handleChange('companyName', e.target.value)}
                    className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-[12px] border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500/30 focus:border-blue-500/50 transition-colors duration-200"
                    placeholder="Enter your company name"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
                  Support Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
                  <input
                    type="email"
                    value={settings.supportEmail}
                    onChange={(e) => handleChange('supportEmail', e.target.value)}
                    className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-[12px] border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500/30 focus:border-blue-500/50 transition-colors duration-200"
                    placeholder="support@company.com"
                  />
                </div>
              </div>
            </div>
            <div className="mt-4 sm:mt-6">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
                Website URL
              </label>
              <div className="relative">
                <Globe className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
                <input
                  type="url"
                  value={settings.websiteUrl}
                  onChange={(e) => handleChange('websiteUrl', e.target.value)}
                  className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-[12px] border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500/30 focus:border-blue-500/50 transition-colors duration-200"
                  placeholder="https://yourwebsite.com"
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 sm:mt-2">
                The website where your chat widget will be installed
              </p>
            </div>
          </div>
        </div>

        {/* Localization */}
        <div className="rounded-[20px] border bg-white dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.06]">
          <div className="px-5 sm:px-6 md:px-8 py-4 sm:py-5 border-b border-gray-100 dark:border-white/[0.06]">
            <div className="flex items-start sm:items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 rounded-[10px] flex items-center justify-center bg-gray-50 dark:bg-white/[0.04] flex-shrink-0">
                <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 dark:text-gray-500" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-white">Localization</h3>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-500 font-light mt-0.5 sm:mt-1">Configure your timezone and language preferences</p>
              </div>
            </div>
          </div>
          <div className="p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
                  Timezone
                </label>
                <div className="relative" ref={timezoneRef}>
                  <button
                    type="button"
                    onClick={() => { setTimezoneOpen(!timezoneOpen); setLanguageOpen(false); }}
                    className="w-full flex items-center pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-[12px] border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] text-gray-900 dark:text-white transition-colors duration-200 text-left"
                  >
                    <Clock className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 dark:text-gray-600" />
                    <span className="flex-1 truncate">{timezoneOptions.find(o => o.value === settings.timezone)?.label || 'Select timezone'}</span>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${timezoneOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {timezoneOpen && (
                    <div className="absolute z-50 mt-1.5 w-full rounded-[12px] border border-gray-200 dark:border-white/[0.06] bg-white dark:bg-[#111] shadow-xl overflow-y-auto max-h-64">
                      {timezoneOptions.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => { handleChange('timezone', opt.value); setTimezoneOpen(false); }}
                          className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                            settings.timezone === opt.value
                              ? 'bg-black/[0.04] dark:bg-white/[0.06] text-gray-900 dark:text-white font-medium'
                              : 'text-gray-600 dark:text-gray-400 hover:bg-black/[0.02] dark:hover:bg-white/[0.03]'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
                  Language
                </label>
                <div className="relative" ref={languageRef}>
                  <button
                    type="button"
                    onClick={() => { setLanguageOpen(!languageOpen); setTimezoneOpen(false); }}
                    className="w-full flex items-center pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-[12px] border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] text-gray-900 dark:text-white transition-colors duration-200 text-left"
                  >
                    <Globe className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 dark:text-gray-600" />
                    <span className="flex-1 truncate">{languageOptions.find(o => o.value === settings.language)?.label || 'Select language'}</span>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${languageOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {languageOpen && (
                    <div className="absolute z-50 mt-1.5 w-full rounded-[12px] border border-gray-200 dark:border-white/[0.06] bg-white dark:bg-[#111] shadow-xl overflow-y-auto max-h-64">
                      {languageOptions.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => { handleChange('language', opt.value); setLanguageOpen(false); }}
                          className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                            settings.language === opt.value
                              ? 'bg-black/[0.04] dark:bg-white/[0.06] text-gray-900 dark:text-white font-medium'
                              : 'text-gray-600 dark:text-gray-400 hover:bg-black/[0.02] dark:hover:bg-white/[0.03]'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="rounded-[20px] border bg-white dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.06] overflow-hidden">
          <div className="px-5 sm:px-6 md:px-8 py-4 sm:py-5 border-b border-gray-100 dark:border-white/[0.06]">
            <div className="flex items-start sm:items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 rounded-[10px] flex items-center justify-center bg-gray-50 dark:bg-white/[0.04] flex-shrink-0">
                <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 dark:text-gray-500" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-white">Notifications</h3>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-500 font-light mt-0.5 sm:mt-1">Configure how you receive notifications and alerts</p>
              </div>
            </div>
          </div>
          <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
            <div className="rounded-[16px] bg-gray-50 dark:bg-white/[0.03] p-4 sm:p-6">
              <div className="flex items-start sm:items-center justify-between gap-4">
                <div className="flex items-start sm:items-center gap-3 sm:gap-4 min-w-0 flex-1">
                  <div className="w-9 h-9 rounded-[10px] flex items-center justify-center bg-gray-50 dark:bg-white/[0.04] flex-shrink-0">
                    <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">Email Notifications</h4>
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-500 font-light mt-0.5 sm:mt-1">Receive email alerts for new customer messages and important updates</p>
                  </div>
                </div>
                <button
                  onClick={() => handleChange('emailNotifications', !settings.emailNotifications)}
                  className={`relative inline-flex h-6 w-11 sm:h-7 sm:w-12 items-center rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex-shrink-0 ${
                    settings.emailNotifications ? 'bg-blue-500 shadow-md' : 'bg-gray-200 dark:bg-white/[0.1]'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
                      settings.emailNotifications ? 'translate-x-6 sm:translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Change Password */}
        <ChangePasswordSection />

        {/* Deactivate Account */}
        <div className="rounded-[20px] border bg-white dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.06] overflow-hidden">
          <div className="px-5 sm:px-6 md:px-8 py-4 sm:py-5 border-b border-gray-100 dark:border-white/[0.06]">
            <div className="flex items-start sm:items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 rounded-[10px] flex items-center justify-center bg-gray-50 dark:bg-white/[0.04] flex-shrink-0">
                <UserX className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 dark:text-gray-500" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-white">Deactivate Account</h3>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-500 font-light mt-0.5 sm:mt-1">Temporarily suspend your account</p>
              </div>
            </div>
          </div>

          <div className="p-4 sm:p-6 md:p-8">
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-500 font-light mb-3 sm:mb-4 leading-relaxed">
              Deactivating your account will temporarily suspend access to all features. You can reactivate anytime within 90 days.
            </p>
            <div className="rounded-[12px] bg-gray-50 dark:bg-white/[0.03] p-3 sm:p-4 mb-4 sm:mb-6">
              <ul className="text-xs sm:text-sm text-secondary space-y-1.5 sm:space-y-2">
                <li className="flex items-start">
                  <span className="text-gray-400 mr-2">•</span>
                  <span>Your account will be immediately suspended</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-400 mr-2">•</span>
                  <span>All widgets will stop working</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-400 mr-2">•</span>
                  <span>Your data will be preserved for 90 days</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-400 mr-2">•</span>
                  <span>You can reactivate anytime within 90 days</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-400 mr-2">•</span>
                  <span><strong>After 90 days, all data will be permanently deleted</strong></span>
                </li>
              </ul>
            </div>
            <div className="flex flex-col xs:flex-row items-start xs:items-center justify-between gap-3 xs:gap-4">
              <button
                onClick={openDeactivateModal}
                className="w-full xs:w-auto px-5 sm:px-6 py-2.5 sm:py-3 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-full font-medium text-sm sm:text-base hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
              >
                Deactivate Account
              </button>
              <span className="text-xs text-tertiary">Can be reversed within 90 days</span>
            </div>
          </div>
        </div>

      </div>

      {/* Deactivate Account Confirmation Modal */}
      {showDeactivateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#111] rounded-[20px] max-w-md w-full mx-4 shadow-2xl border border-gray-200 dark:border-white/[0.06]">
            <div className="p-6 border-b border-gray-100 dark:border-white/[0.06]">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 rounded-[10px] flex items-center justify-center bg-gray-50 dark:bg-white/[0.04]">
                    <AlertTriangle className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Confirm Deactivation</h3>
                </div>
                <button
                  onClick={closeDeactivateModal}
                  className="text-gray-400 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] rounded-[10px] p-1 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <p className="text-sm text-gray-500 dark:text-gray-500 font-light mb-4">
                To confirm account deactivation, please enter your password. Your account will be suspended and all data will be deleted after 90 days.
              </p>

              {hasActiveSubscription() && (
                <div className="flex items-start gap-2 p-3 mb-4 rounded-[12px] bg-red-500/[0.05] border border-red-500/20">
                  <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700 dark:text-red-300">
                    Your active subscription will be cancelled immediately and you will not be charged again.
                  </p>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="password"
                    value={deactivatePassword}
                    onChange={(e) => setDeactivatePassword(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleDeactivateAccount()}
                    className="w-full pl-10 pr-4 py-3 rounded-[12px] border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500/30 focus:border-blue-500/50 transition-colors"
                    placeholder="Enter your password"
                    autoFocus
                  />
                </div>
                {deactivateError && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-2">{deactivateError}</p>
                )}
              </div>

              <div className="rounded-[12px] bg-gray-50 dark:bg-white/[0.03] p-4 mb-6">
                <p className="text-xs text-secondary">
                  <strong>Important:</strong> After deactivation, you have 90 days to reactivate your account by logging in. After 90 days, all your data will be permanently deleted and cannot be recovered.
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={closeDeactivateModal}
                  disabled={isDeactivating}
                  className="flex-1 px-4 py-3 rounded-full bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeactivateAccount}
                  disabled={isDeactivating || !deactivatePassword}
                  className="flex-1 px-4 py-3 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-full font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeactivating ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white dark:border-gray-900 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : (
                    'Deactivate Account'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deactivation Success Modal */}
      {showDeactivateSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#111] rounded-[20px] max-w-md w-full mx-4 shadow-2xl border border-gray-200 dark:border-white/[0.06]">
            <div className="p-8 text-center">
              {/* Success Icon */}
              <div className="mx-auto w-16 h-16 bg-green-500/[0.08] rounded-[14px] flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>

              {/* Success Message */}
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Account Deactivated</h3>
              <p className="text-base text-gray-500 dark:text-gray-500 font-light mb-6 leading-relaxed">
                Your account has been successfully deactivated. You can reactivate it within 90 days by logging in again.
              </p>

              {/* Info Box */}
              <div className="rounded-[12px] bg-blue-500/[0.05] border border-blue-500/20 p-4 mb-6">
                <p className="text-sm text-blue-900 dark:text-blue-100">
                  <strong>Important:</strong> After 90 days, all your data will be permanently deleted and cannot be recovered.
                </p>
              </div>

              {/* Redirect Button */}
              <button
                onClick={() => {
                  setShowDeactivateSuccessModal(false);
                  window.location.href = '/login';
                }}
                className="w-full px-6 py-3 bg-black dark:bg-white text-white dark:text-black rounded-full font-semibold hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors shadow-md"
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unsaved Changes Modal */}
      {!!pendingNavigation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => setPendingNavigation(null)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            className="relative bg-white dark:bg-[#111] rounded-[20px] border border-gray-200 dark:border-white/[0.06] shadow-2xl w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Unsaved Changes</h3>
              <p className="text-sm text-gray-500 dark:text-gray-500 font-light">
                You have unsaved settings changes. Would you like to save before leaving?
              </p>
            </div>
            <div className="border-t border-gray-100 dark:border-white/[0.06] px-6 py-4 flex items-center justify-end gap-2">
              <button
                onClick={() => {
                  const dest = pendingNavigation;
                  setPendingNavigation(null);
                  savedSettingsRef.current = { ...settings };
                  navigate(dest);
                }}
                className="px-4 py-2 text-sm font-medium rounded-full bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-gray-500 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
              >
                Discard
              </button>
              <button
                onClick={async () => {
                  await handleSave();
                  const dest = pendingNavigation;
                  setPendingNavigation(null);
                  navigate(dest);
                }}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium rounded-full bg-blue-500 hover:bg-blue-600 text-white transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneralPage;
